import React, { useEffect } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { Offer } from 'common/contracts';

import { PopupsFormFields } from '../../PopupsForm.consts';
import { ProductEntry } from '../usePopupProducts';

import { prepareInitialFormValues } from './usePopupFormInit.utils';

interface IProgressBarFormValues {
  progressBarId: string | null;
  progressBarPoints: number | null;
}

interface IUsePopupFormInitProps {
  form: UseFormReturn<PopupsFormFields>;
  edit: boolean;
  dup: boolean;
  popupId: string | undefined;
  defaultOfferUiId: string;
  currentOffer: Offer | undefined;
  setDailyProducts: (products: ProductEntry[]) => void;
  setPostProducts: (products: ProductEntry[]) => void;
  setProgressBarFormValues: React.Dispatch<React.SetStateAction<IProgressBarFormValues>>;
}

/**
 * Handles form initialization for edit/duplicate/create flows of the Popup form.
 *
 * @example
 * usePopupFormInit({ form, edit, dup, popupId, defaultOfferUiId, currentOffer, setDailyProducts, setPostProducts });
 */
export const usePopupFormInit = ({
  form,
  edit,
  dup,
  popupId,
  defaultOfferUiId,
  currentOffer,
  setDailyProducts,
  setPostProducts,
  setProgressBarFormValues
}: IUsePopupFormInitProps): void => {
  useEffect(() => {
    if (!popupId && defaultOfferUiId) {
      form.setValue('offerUiId', defaultOfferUiId, {
        shouldDirty: false,
        shouldValidate: true
      });
    }

    if ((edit || dup) && currentOffer) {
      const { isDailySubType, products, formValues } = prepareInitialFormValues(
        currentOffer,
        dup,
        edit,
        defaultOfferUiId
      );
      setDailyProducts(isDailySubType ? products : []);
      setPostProducts(isDailySubType ? [] : products);
      setProgressBarFormValues({
        progressBarPoints:
          currentOffer.productsSequence?.[0]?.progressBarPoints?.[0]?.points ?? null,
        progressBarId:
          currentOffer.productsSequence?.[0]?.progressBarPoints?.[0]?.barId ?? null
      });
      form.reset(formValues);
    }
  }, [
    edit,
    dup,
    currentOffer,
    form,
    popupId,
    defaultOfferUiId,
    setDailyProducts,
    setPostProducts,
    setProgressBarFormValues
  ]);
};
